import { useQuery } from '@tanstack/react-query';
import { metadataAPI } from '../api/enhancedApiService';

/**
 * Custom hook to fetch ALL available schemas directly from the database
 *
 * This queries the database (SHOW SCHEMAS) rather than relying on cached tables.
 * Returns schemas even if they have no cached metadata yet.
 *
 * Enables selecting brand new schemas or empty schemas for monitoring.
 *
 * @param {string} connectionId - The connection ID to fetch schemas for
 * @param {Object} options - Additional options for the query
 * @returns {Object} Query result object with schemas list
 */
export const useAvailableSchemas = (connectionId, options = {}) => {
  const {
    enabled = !!connectionId,
    refetchInterval = false,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['available-schemas', connectionId],
    queryFn: async ({ signal }) => {
      const response = await metadataAPI.getAvailableSchemas(connectionId, { signal });
      return response;
    },
    enabled: enabled,
    refetchInterval: refetchInterval,
    // Cache for 5 minutes - schema structure doesn't change frequently
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
    select: (data) => {
      return {
        schemas: data?.schemas || [],
        count: data?.count || 0
      };
    }
  });
};
